import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';
import { Plus, X, AlertTriangle, Edit, Search, Check, ChevronsUpDown, Package, TrendingUp, Clock, CheckCircle2, RefreshCw, ShoppingCart, Truck, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Pagination, ITEMS_PER_PAGE } from '@/components/Pagination';
import { TableSkeleton } from '@/components/ui/Skeletons';
import StatusTimeline from '@/components/ui/StatusTimeline';
import ConfirmDialog from '@/components/ui/ConfirmDialog';

const generateSONumber = (existingOrders = []) => {
  const date = new Date();
  const yymmdd = new Intl.DateTimeFormat('en-IN', {
    year: '2-digit', month: '2-digit', day: '2-digit'
  }).format(date).replace(/\//g, '');

  const prefix = `SO-${yymmdd}-`;
  let maxCount = 0;

  existingOrders.forEach(order => {
    if (order.so_number && order.so_number.startsWith(prefix)) {
      const parts = order.so_number.split('-');
      if (parts.length === 3) {
        const num = parseInt(parts[2], 10);
        if (!isNaN(num) && num > maxCount) {
          maxCount = num;
        }
      }
    }
  });

  const nextCount = (maxCount + 1).toString().padStart(2, '0');
  return `${prefix}${nextCount}`;
};

const SalesOrders = ({ user }) => {
  const soPerms = user?.permissions?.sales_orders || [];
  const canCreate = user?.role === 'admin' || soPerms.includes('create');
  const canEdit = user?.role === 'admin' || soPerms.includes('edit');

  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [users, setUsers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [dispatchDialogOpen, setDispatchDialogOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [dispatchData, setDispatchData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [submitting, setSubmitting] = useState(false);

  // Confirmation Dialog State
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [formData, setFormData] = useState({
    so_number: '',
    customer: '',
    expected_delivery_date: '',
    assigned_to: '',
    location: '',
    items: [],
  });
  const [sellStockDialogOpen, setSellStockDialogOpen] = useState(false);
  const [openProductPopover, setOpenProductPopover] = useState({});
  const [openSellProductPopover, setOpenSellProductPopover] = useState({});
  const [sellStockFormData, setSellStockFormData] = useState({
    so_number: '',
    customer: '',
    expected_delivery_date: '',
    assigned_to: '',
    location: '',
    items: [],
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [ordersRes, productsRes, usersRes, customersRes, locationsRes] = await Promise.all([
        axios.get(`${API}/sales-orders`),
        axios.get(`${API}/finished-goods`),
        axios.get(`${API}/users/list`),
        axios.get(`${API}/customers`),
        axios.get(`${API}/warehouse-locations`),
      ]);
      setOrders(ordersRes.data);
      setProducts(productsRes.data);
      setUsers(usersRes.data);
      setCustomers(customersRes.data);
      setLocations(locationsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      so_number: '',
      customer: '',
      expected_delivery_date: '',
      assigned_to: '',
      location: '',
      items: [],
    });
  };

  const resetSellStockForm = () => {
    setSellStockFormData({
      so_number: '',
      customer: '',
      expected_delivery_date: '',
      assigned_to: '',
      location: '',
      items: [],
    });
  };

  const handleSellStockSubmit = async (e) => {
    e.preventDefault();
    if (!sellStockFormData.location) {
      toast.error('Location is required');
      return;
    }
    setSubmitting(true);
    try {
      const expDate = sellStockFormData.expected_delivery_date ? new Date(sellStockFormData.expected_delivery_date).toISOString() : null;
      const data = {
        ...sellStockFormData,
        expected_delivery_date: expDate,
        assigned_to: sellStockFormData.assigned_to || '',
        location: sellStockFormData.location || '',
        items: sellStockFormData.items.map((item) => ({
          ...item,
          quantity: parseFloat(item.quantity)
        })),
      };

      await axios.post(`${API}/sales-orders/sell-stock`, data);
      toast.success(`Stock sold successfully.`);
      setSellStockDialogOpen(false);
      resetSellStockForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'An error occurred during sell stock');
    } finally {
      setSubmitting(false);
    }
  };

  const addSellStockItem = () => {
    setSellStockFormData({
      ...sellStockFormData,
      items: [...sellStockFormData.items, { product_id: '', product_name: '', quantity: '' }],
    });
  };

  const removeSellStockItem = (index) => {
    const updated = sellStockFormData.items.filter((_, i) => i !== index);
    setSellStockFormData({ ...sellStockFormData, items: updated });
  };

  const updateSellStockItem = (index, field, value) => {
    const updated = [...sellStockFormData.items];
    updated[index][field] = value;
    if (field === 'product_id') {
      const prod = products.find((p) => p.id === value);
      if (prod) {
        updated[index].product_name = prod.name;
      }
    }
    setSellStockFormData({ ...sellStockFormData, items: updated });
  };

  const handleEdit = (order) => {
    setEditingOrder(order);
    setEditDialogOpen(true);
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    if (!editingOrder.location) {
      toast.error('Location is required');
      return;
    }
    try {
      const expDate = editingOrder.expected_delivery_date ? new Date(editingOrder.expected_delivery_date).toISOString() : null;
      await axios.put(`${API}/sales-orders/${editingOrder.id}`, {
        customer: editingOrder.customer,
        expected_delivery_date: expDate,
        assigned_to: editingOrder.assigned_to || '',
        location: editingOrder.location || ''
      });
      toast.success('Sales order updated');
      setEditDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to update sales order');
    }
  };

  const openDispatch = (order) => {
    setEditingOrder(order);
    setDispatchData(order.items.map(item => ({
      product_id: item.product_id,
      product_name: typeof item.product_name !== 'undefined' ? item.product_name : '',
      ordered: item.quantity,
      supplied: item.supplied_qty,
      dispatch: 0
    })));
    setDispatchDialogOpen(true);
  };

  const submitDispatch = async (e) => {
    e.preventDefault();
    try {
      const payload = dispatchData.filter(d => d.dispatch > 0).map(d => ({
        product_id: d.product_id,
        quantity: parseFloat(d.dispatch)
      }));

      if (payload.length === 0) {
        toast.error('Please enter a quantity greater than 0 to dispatch');
        return;
      }

      await axios.put(`${API}/sales-orders/${editingOrder.id}/dispatch`, { items: payload });
      toast.success('Successfully dispatched items!');
      setDispatchDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to dispatch items');
    }
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { product_id: '', product_name: '', quantity: '', supplied_qty: 0 }],
    });
  };

  const removeItem = (index) => {
    const updated = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: updated });
  };

  const updateItem = (index, field, value) => {
    const updated = [...formData.items];
    updated[index][field] = value;

    if (field === 'product_id') {
      const prod = products.find((p) => p.id === value);
      if (prod) {
        updated[index].product_name = prod.name;
      }
    }

    setFormData({ ...formData, items: updated });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.location) {
      toast.error('Location is required');
      return;
    }
    setSubmitting(true);
    try {
      const expDate = formData.expected_delivery_date ? new Date(formData.expected_delivery_date).toISOString() : null;
      const data = {
        ...formData,
        expected_delivery_date: expDate,
        assigned_to: formData.assigned_to || '',
        location: formData.location || '',
        items: formData.items.map((item) => ({
          ...item,
          quantity: parseFloat(item.quantity),
          supplied_qty: 0,
        })),
      };

      const response = await axios.post(`${API}/sales-orders`, data);

      if (response.data.warnings && response.data.warnings.length > 0) {
        toast.warning('Order created with warnings', {
          description: response.data.warnings.join(', '),
          duration: 8000,
        });
      } else {
        toast.success(`Sales order created. ${response.data.production_orders?.length || 0} production order(s) auto-generated.`);
      }

      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'An error occurred');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = (order) => {
    setOrderToDelete(order);
    setConfirmOpen(true);
  };

  const executeDelete = async () => {
    if (!orderToDelete) return;
    setIsDeleting(true);
    try {
      const res = await axios.delete(`${API}/sales-orders/${orderToDelete.id}`);
      const poCount = res.data.production_orders_deleted || 0;
      toast.success(`Sales order deleted. ${poCount} linked production order(s) removed.`);
      fetchData();
      setConfirmOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete sales order');
    } finally {
      setIsDeleting(false);
      setOrderToDelete(null);
    }
  };

  if (loading) {
    return (
      <div className="p-8 max-w-7xl mx-auto space-y-6">
        <div className="flex justify-between items-center mb-8">
          <div>
            <div className="h-10 w-64 bg-stone-200/60 rounded animate-pulse mb-2"></div>
            <div className="h-4 w-96 bg-stone-200/60 rounded animate-pulse"></div>
          </div>
          <div className="h-10 w-40 bg-stone-200/60 rounded animate-pulse"></div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white rounded-xl border border-stone-200/60 p-5 shadow-sm h-24 animate-pulse"></div>
          ))}
        </div>

        <div className="bg-white rounded-xl border border-stone-200/80 shadow-sm p-6">
          <TableSkeleton rows={5} columns={7} />
        </div>
      </div>
    );
  }

  const totalOrders = orders.length;
  const completedOrders = orders.filter(o => o.status === 'completed').length;
  const pendingOrders = totalOrders - completedOrders;

  return (
    <div className="p-8 max-w-7xl mx-auto" data-testid="sales-orders-page">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <h1 className="text-4xl font-bold text-stone-800 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Sales Orders</h1>
          <p className="text-sm text-stone-500 mt-2 font-medium">Manage customer orders, track shipments, and dispatch inventory.</p>
        </div>
        <div className="flex gap-4">
          {canCreate && <Dialog open={sellStockDialogOpen} onOpenChange={(open) => { setSellStockDialogOpen(open); if (!open) resetSellStockForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-white hover:bg-stone-50 text-stone-700 border border-stone-200 shadow-sm transition-all" data-testid="sell-stock-button">
                <Package size={16} className="mr-2 text-indigo-500" />
                Direct Sell Stock
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Sell Stock Directly</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSellStockSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="ss_so_number">SO Number</Label>
                    <div className="flex gap-2 mt-1.5">
                      <Input
                        id="ss_so_number"
                        value={sellStockFormData.so_number}
                        onChange={(e) => setSellStockFormData({ ...sellStockFormData, so_number: e.target.value })}
                        required
                        className="flex-1"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setSellStockFormData({ ...sellStockFormData, so_number: generateSONumber(orders) })}
                        title="Auto-generate"
                        className="px-3"
                      >
                        <RefreshCw size={16} />
                      </Button>
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="ss_customer">Customer Name</Label>
                    <Select value={sellStockFormData.customer} onValueChange={(value) => setSellStockFormData({ ...sellStockFormData, customer: value })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select customer" />
                      </SelectTrigger>
                      <SelectContent>
                        {customers.map((c) => (
                          <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="ss_expected_delivery_date">Expected Delivery Date</Label>
                    <Input
                      id="ss_expected_delivery_date"
                      type="date"
                      value={sellStockFormData.expected_delivery_date}
                      onChange={(e) => setSellStockFormData({ ...sellStockFormData, expected_delivery_date: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="ss_assigned_to">Assign To Team Member</Label>
                    <Select value={sellStockFormData.assigned_to} onValueChange={(value) => setSellStockFormData({ ...sellStockFormData, assigned_to: value })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select team member" />
                      </SelectTrigger>
                      <SelectContent>
                        {users.map((u) => (
                          <SelectItem key={u.id} value={u.id}>{u.name} ({u.role})</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="ss_location">Location <span className="text-red-500">*</span></Label>
                    <Select value={sellStockFormData.location} onValueChange={(value) => setSellStockFormData({ ...sellStockFormData, location: value })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select location" />
                      </SelectTrigger>
                      <SelectContent>
                        {locations.map((l) => (
                          <SelectItem key={l.id} value={l.name}>{l.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-2">
                    <Label>Finished Goods to Sell</Label>
                    <Button type="button" variant="outline" size="sm" onClick={addSellStockItem}>
                      <Plus size={14} className="mr-1" /> Add Product
                    </Button>
                  </div>
                  <div className="space-y-3">
                    {sellStockFormData.items.map((item, index) => {
                      const stockItem = products.find(p => p.id === item.product_id);
                      const maxStock = stockItem ? stockItem.current_stock : 0;
                      return (
                        <div key={index} className="flex gap-2 items-end">
                          <div className="flex-1">
                            <Label>Product (In Stock)</Label>
                            <Popover open={openSellProductPopover[index]} onOpenChange={(open) => setOpenSellProductPopover(prev => ({ ...prev, [index]: open }))}>
                              <PopoverTrigger asChild>
                                <Button
                                  variant="outline"
                                  role="combobox"
                                  aria-expanded={openSellProductPopover[index]}
                                  className="w-full justify-between font-normal"
                                >
                                  {item.product_id
                                    ? (() => {
                                      const p = products.find(pr => pr.id === item.product_id);
                                      return p ? `${p.name} (${p.current_stock} available)` : 'Select product';
                                    })()
                                    : 'Select product'}
                                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-[300px] p-0" align="start">
                                <Command>
                                  <CommandInput
                                    placeholder="Search products..."
                                    onValueChange={(search) => {
                                      // Allow searching normally, but if exact product code typed, auto-select
                                      const exactMatch = products.filter(p => p.current_stock > 0).find(p => p.product_code && p.product_code.toLowerCase() === search.toLowerCase());
                                      if (exactMatch) {
                                        updateSellStockItem(index, 'product_id', exactMatch.id);
                                        setOpenSellProductPopover(prev => ({ ...prev, [index]: false }));
                                      }
                                    }}
                                  />
                                  <CommandList>
                                    <CommandEmpty>No product found.</CommandEmpty>
                                    <CommandGroup>
                                      {products.filter(p => p.current_stock > 0).map((prod) => (
                                        <CommandItem
                                          key={prod.id}
                                          value={`${prod.name} ${prod.product_code}`}
                                          onSelect={() => {
                                            updateSellStockItem(index, 'product_id', prod.id);
                                            setOpenSellProductPopover(prev => ({ ...prev, [index]: false }));
                                          }}
                                        >
                                          <Check className={`mr-2 h-4 w-4 ${item.product_id === prod.id ? 'opacity-100' : 'opacity-0'}`} />
                                          {prod.name} ({prod.current_stock} available)
                                        </CommandItem>
                                      ))}
                                    </CommandGroup>
                                  </CommandList>
                                </Command>
                              </PopoverContent>
                            </Popover>
                          </div>
                          <div className="w-32">
                            <Label>Quantity</Label>
                            <Input
                              type="number"
                              step="0.01"
                              min="0.01"
                              max={maxStock > 0 ? maxStock : ""}
                              value={item.quantity}
                              onChange={(e) => updateSellStockItem(index, 'quantity', e.target.value)}
                              required
                            />
                          </div>
                          <Button type="button" variant="ghost" size="sm" onClick={() => removeSellStockItem(index)}>
                            <X size={16} />
                          </Button>
                        </div>
                      )
                    })}
                  </div>
                </div>

                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setSellStockDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" disabled={submitting} className="bg-stone-800 hover:bg-stone-900 text-white rounded-md px-6 shadow-sm transition-all">
                    {submitting ? 'Processing...' : 'Confirm Sale'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>}

          {canCreate && <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-indigo-600 hover:bg-indigo-700 text-white shadow shadow-indigo-200 transition-all font-medium" data-testid="add-so-button">
                <Plus size={16} className="mr-2" />
                New Sales Order
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Create Sales Order</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="so-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="so_number">SO Number</Label>
                    <div className="flex gap-2 mt-1.5">
                      <Input
                        id="so_number"
                        value={formData.so_number}
                        onChange={(e) => setFormData({ ...formData, so_number: e.target.value })}
                        required
                        data-testid="input-so-number"
                        className="flex-1"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setFormData({ ...formData, so_number: generateSONumber(orders) })}
                        title="Auto-generate"
                        className="px-3"
                      >
                        <RefreshCw size={16} />
                      </Button>
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="customer">Customer Name</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          className="w-full justify-between font-normal"
                          data-testid="input-customer"
                        >
                          {formData.customer
                            ? formData.customer
                            : "Select customer"}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[300px] p-0" align="start">
                        <Command>
                          <CommandInput placeholder="Search customer..." />
                          <CommandList>
                            <CommandEmpty>No customer found.</CommandEmpty>
                            <CommandGroup>
                              {customers.map((c) => (
                                <CommandItem
                                  key={c.id}
                                  value={c.name}
                                  onSelect={(currentValue) => {
                                    setFormData({ ...formData, customer: c.name });
                                  }}
                                >
                                  <Check className={`mr-2 h-4 w-4 ${formData.customer === c.name ? 'opacity-100' : 'opacity-0'}`} />
                                  {c.name}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="expected_delivery_date">Expected Delivery Date (Optional)</Label>
                    <Input
                      id="expected_delivery_date"
                      type="date"
                      value={formData.expected_delivery_date}
                      onChange={(e) => setFormData({ ...formData, expected_delivery_date: e.target.value })}
                      data-testid="input-expected-date"
                    />
                  </div>
                  <div>
                    <Label htmlFor="assigned_to">Assign To Team Member</Label>
                    <Select value={formData.assigned_to} onValueChange={(value) => setFormData({ ...formData, assigned_to: value })}>
                      <SelectTrigger data-testid="select-assigned-to">
                        <SelectValue placeholder="Select team member" />
                      </SelectTrigger>
                      <SelectContent>
                        {users.map((u) => (
                          <SelectItem key={u.id} value={u.id}>{u.name} ({u.role})</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="location">Location <span className="text-red-500">*</span></Label>
                    <Select value={formData.location} onValueChange={(value) => setFormData({ ...formData, location: value })}>
                      <SelectTrigger data-testid="select-location">
                        <SelectValue placeholder="Select location" />
                      </SelectTrigger>
                      <SelectContent>
                        {locations.map((l) => (
                          <SelectItem key={l.id} value={l.name}>{l.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-2">
                    <Label>Products</Label>
                    <Button type="button" variant="outline" size="sm" onClick={addItem} data-testid="add-so-item">
                      <Plus size={14} className="mr-1" /> Add Product
                    </Button>
                  </div>
                  <div className="space-y-3">
                    {formData.items.map((item, index) => (
                      <div key={index} className="flex flex-col sm:flex-row gap-2 items-start sm:items-end bg-stone-50/50 p-3 rounded-lg border border-stone-100">
                        <div className="flex-1 w-full sm:w-auto relative mb-4 sm:mb-0">
                          <Label className="mb-1 block">Product</Label>
                          <Popover open={openProductPopover[index]} onOpenChange={(open) => setOpenProductPopover(prev => ({ ...prev, [index]: open }))}>
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                role="combobox"
                                aria-expanded={openProductPopover[index]}
                                className="w-full justify-between font-normal bg-white"
                                data-testid={`select-so-product-${index}`}
                              >
                                {item.product_id
                                  ? (() => {
                                    const p = products.find(pr => pr.id === item.product_id);
                                    return p ? `${p.name} (${p.product_code})` : 'Select product';
                                  })()
                                  : 'Select product'}
                                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-[300px] sm:w-[400px] p-0 shadow-lg border-stone-200" align="start">
                              <Command>
                                <CommandInput
                                  placeholder="Search products..."
                                  className="h-9"
                                  onValueChange={(search) => {
                                    const exactMatch = products.find(p => p.product_code && p.product_code.toLowerCase() === search.toLowerCase());
                                    if (exactMatch) {
                                      updateItem(index, 'product_id', exactMatch.id);
                                      setOpenProductPopover(prev => ({ ...prev, [index]: false }));
                                    }
                                  }}
                                />
                                <CommandList>
                                  <CommandEmpty>No product found.</CommandEmpty>
                                  <CommandGroup>
                                    {products.map((prod) => (
                                      <CommandItem
                                        key={prod.id}
                                        value={`${prod.name} ${prod.product_code}`}
                                        onSelect={() => {
                                          updateItem(index, 'product_id', prod.id);
                                          setOpenProductPopover(prev => ({ ...prev, [index]: false }));
                                        }}
                                      >
                                        <Check className={`mr-2 h-4 w-4 ${item.product_id === prod.id ? 'opacity-100' : 'opacity-0'}`} />
                                        {prod.name} ({prod.product_code})
                                      </CommandItem>
                                    ))}
                                  </CommandGroup>
                                </CommandList>
                              </Command>
                            </PopoverContent>
                          </Popover>
                        </div>
                        <div className="w-full sm:w-32">
                          <Label className="mb-1 block">Quantity</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={item.quantity}
                            onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                            required
                            data-testid={`input-so-quantity-${index}`}
                          />
                        </div>
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeItem(index)} className="w-full sm:w-auto justify-center mt-2 sm:mt-0" data-testid={`remove-so-item-${index}`}>
                          <X size={16} />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="w-full sm:w-auto">Cancel</Button>
                  <Button type="submit" disabled={submitting} className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700 text-white shadow shadow-indigo-200 rounded-md px-6" data-testid="submit-so-button">
                    {submitting ? 'Creating...' : 'Create Order'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-xl border border-stone-200/60 p-5 shadow-sm flex items-center gap-4">
          <div className="p-3 bg-blue-50 text-blue-600 rounded-lg">
            <TrendingUp size={24} />
          </div>
          <div>
            <p className="text-sm font-medium text-stone-500">Total Orders</p>
            <p className="text-2xl font-bold text-stone-800">{totalOrders}</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-stone-200/60 p-5 shadow-sm flex items-center gap-4">
          <div className="p-3 bg-amber-50 text-amber-600 rounded-lg">
            <Clock size={24} />
          </div>
          <div>
            <p className="text-sm font-medium text-stone-500">Pending Fulfillment</p>
            <p className="text-2xl font-bold text-stone-800">{pendingOrders}</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-stone-200/60 p-5 shadow-sm flex items-center gap-4">
          <div className="p-3 bg-emerald-50 text-emerald-600 rounded-lg">
            <CheckCircle2 size={24} />
          </div>
          <div>
            <p className="text-sm font-medium text-stone-500">Completed</p>
            <p className="text-2xl font-bold text-stone-800">{completedOrders}</p>
          </div>
        </div>
      </div>

      <div className="mb-6 flex items-center justify-between">
        <div className="relative w-full max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-stone-400" size={18} />
          <Input
            placeholder="Search by SO number or customer..."
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            className="pl-10 bg-white border-stone-200 focus-visible:ring-indigo-500 shadow-sm rounded-lg"
          />
        </div>
      </div>

      <div className="bg-white rounded-xl border border-stone-200/80 shadow-sm overflow-hidden mb-6">
        {/* Desktop Table View */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full" data-testid="so-table">
            <thead className="bg-stone-50/80 border-b border-stone-100/80">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">SO Number</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">Customer</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">Date</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">Assigned To</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">Items Overview</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-stone-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {(() => {
                const filtered = orders.filter(o => {
                  if (!searchTerm) return true;
                  const q = searchTerm.toLowerCase();
                  return o.so_number.toLowerCase().includes(q) || o.customer.toLowerCase().includes(q);
                });
                const paginated = filtered.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
                return paginated.map((order) => {
                  const hasPending = order.items.some(item => item.supplied_qty < item.quantity);
                  const isCompleted = order.status === 'completed';
                  return (
                    <tr key={order.id} className="hover:bg-indigo-50/30 transition-colors group" data-testid={`so-row-${order.so_number}`}>
                      <td className="px-6 py-4 text-sm font-medium text-indigo-900">{order.so_number}</td>
                      <td className="px-6 py-4 text-sm text-stone-700 font-medium">{order.customer}</td>
                      <td className="px-6 py-4 text-sm text-stone-500">{new Date(order.date).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}</td>
                      <td className="px-6 py-4 text-sm text-stone-500">
                        {order.assigned_to ? users.find(u => u.id === order.assigned_to)?.name || 'N/A' : 'Unassigned'}
                      </td>
                      <td className="px-6 py-4">
                        <div className="space-y-1">
                          {order.items.map((item, i) => (
                            <div key={i} className="flex items-center text-xs">
                              <span className="text-stone-700 font-medium truncate max-w-[120px]" title={item.product_name}>{item.product_name || 'Item'}</span>
                              <span className="mx-2 text-stone-300">-</span>
                              <span className={`font-mono ${item.supplied_qty < item.quantity ? 'text-amber-600' : 'text-emerald-600'}`}>
                                {item.supplied_qty} / {item.quantity}
                              </span>
                            </div>
                          ))}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col gap-2">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border w-fit ${isCompleted ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${isCompleted ? 'bg-emerald-500' : 'bg-amber-500'}`}></span>
                            {isCompleted ? 'Completed' : 'Pending'}
                          </span>
                          <div className="mt-1 w-48">
                            <StatusTimeline
                              stages={['ordered', 'processing', 'dispatched', 'completed']}
                              currentStage={isCompleted ? 'completed' : (hasPending ? 'processing' : 'ordered')}
                              completedStages={isCompleted ? ['ordered', 'processing', 'dispatched', 'completed'] : ['ordered']}
                              isVertical={false}
                            />
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          {canEdit && (
                            <Button variant="ghost" size="sm" onClick={() => handleEdit(order)} className="h-8 w-8 p-0 text-stone-400 hover:text-indigo-600 hover:bg-indigo-50" title="Edit Order">
                              <Edit size={14} />
                            </Button>
                          )}
                          {canEdit && !isCompleted && (
                            <Button variant="outline" size="sm" onClick={() => openDispatch(order)} className="h-8 text-xs font-medium bg-white text-indigo-600 border-indigo-200 hover:bg-indigo-50" title="Dispatch Items">
                              Dispatch
                            </Button>
                          )}
                          {user?.role === 'admin' && (
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteClick(order)} className="h-8 w-8 p-0 text-stone-400 hover:text-red-600 hover:bg-red-50" title="Delete Order">
                              <Trash2 size={14} />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                });
              })()}
            </tbody>
          </table>
        </div>

        {/* Mobile Card View */}
        <div className="md:hidden flex flex-col divide-y divide-stone-100">
          {(() => {
            const filtered = orders.filter(o => {
              if (!searchTerm) return true;
              const q = searchTerm.toLowerCase();
              return o.so_number.toLowerCase().includes(q) || o.customer.toLowerCase().includes(q);
            });
            return filtered.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE).map((order) => {
              const hasPending = order.items.some(item => item.supplied_qty < item.quantity);
              const isCompleted = order.status === 'completed';
              return (
                <div key={`mobile-${order.id}`} className="p-4 bg-white space-y-3">
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-semibold text-indigo-900">{order.so_number}</h4>
                      <p className="text-sm font-medium text-stone-700">{order.customer}</p>
                    </div>
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-medium border ${isCompleted ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
                      {isCompleted ? 'Completed' : 'Pending'}
                    </span>
                  </div>

                  <div className="py-2">
                    <StatusTimeline
                      stages={['ordered', 'processing', 'dispatched', 'completed']}
                      currentStage={isCompleted ? 'completed' : (hasPending ? 'processing' : 'ordered')}
                      completedStages={isCompleted ? ['ordered', 'processing', 'dispatched', 'completed'] : ['ordered']}
                      isVertical={false}
                    />
                  </div>

                  <div className="flex justify-between text-xs text-stone-500">
                    <span>Date: {new Date(order.date).toLocaleDateString()}</span>
                    <span>Assignee: {order.assigned_to ? users.find(u => u.id === order.assigned_to)?.name || 'N/A' : 'Unassigned'}</span>
                  </div>
                  <div className="bg-stone-50 rounded-md p-2">
                    <p className="text-xs font-medium text-stone-700 mb-1 border-b border-stone-200 pb-1">Items</p>
                    <div className="space-y-1">
                      {order.items.map((item, i) => (
                        <div key={i} className="flex justify-between items-center text-[10px]">
                          <span className="text-stone-700 truncate pr-2 max-w-[150px]">{item.product_name || 'Item'}</span>
                          <span className={`font-mono ${item.supplied_qty < item.quantity ? 'text-amber-600' : 'text-emerald-600'}`}>
                            {item.supplied_qty} / {item.quantity}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="flex justify-end gap-2 pt-2">
                    {canEdit && (
                      <Button variant="outline" size="sm" onClick={() => handleEdit(order)} className="flex-1 h-8 text-xs text-indigo-600">
                        <Edit size={14} className="mr-1" /> Edit
                      </Button>
                    )}
                    {canEdit && !isCompleted && (
                      <Button variant="outline" size="sm" onClick={() => openDispatch(order)} className="flex-1 h-8 text-xs text-indigo-600">
                        Dispatch
                      </Button>
                    )}
                    {user?.role === 'admin' && (
                      <Button variant="outline" size="sm" onClick={() => handleDeleteClick(order)} className="h-8 w-8 p-0 text-red-600 border-red-200 bg-red-50">
                        <Trash2 size={14} />
                      </Button>
                    )}
                  </div>
                </div>
              );
            });
          })()}
        </div>
      </div>
      <div className="flex justify-center mt-2">
        <Pagination currentPage={currentPage} totalItems={orders.filter(o => { if (!searchTerm) return true; const q = searchTerm.toLowerCase(); return o.so_number.toLowerCase().includes(q) || o.customer.toLowerCase().includes(q); }).length} onPageChange={setCurrentPage} />
      </div>

      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Sales Order</DialogTitle>
          </DialogHeader>
          {editingOrder && (
            <form onSubmit={submitEdit} className="space-y-4">
              <div>
                <Label>SO Number</Label>
                <Input value={editingOrder.so_number} disabled />
              </div>
              <div>
                <Label htmlFor="edit_customer">Customer Name</Label>
                <Select value={editingOrder.customer} onValueChange={(value) => setEditingOrder({ ...editingOrder, customer: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select customer" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map((c) => (
                      <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="edit_expected_date">Expected Delivery Date</Label>
                <Input
                  id="edit_expected_date"
                  type="date"
                  value={editingOrder.expected_delivery_date ? new Date(editingOrder.expected_delivery_date).toISOString().split('T')[0] : ''}
                  onChange={(e) => setEditingOrder({ ...editingOrder, expected_delivery_date: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="edit_assigned_to">Assign To</Label>
                <Select value={editingOrder.assigned_to || 'unassigned'} onValueChange={(value) => setEditingOrder({ ...editingOrder, assigned_to: value === 'unassigned' ? '' : value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select team member" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {users.map((u) => (
                      <SelectItem key={u.id} value={u.id}>{u.name} ({u.role})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="edit_location">Location <span className="text-red-500">*</span></Label>
                <Select value={editingOrder.location || 'unassigned'} onValueChange={(value) => setEditingOrder({ ...editingOrder, location: value === 'unassigned' ? '' : value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select location" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {locations.map((l) => (
                      <SelectItem key={l.id} value={l.name}>{l.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
                <Button type="submit">Update Order</Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={dispatchDialogOpen} onOpenChange={setDispatchDialogOpen}>
        <DialogContent className="max-w-2xl w-[95vw] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Dispatch Items for {editingOrder?.so_number}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2 pb-2">
            {dispatchData.map((item, index) => {
              const remaining = item.ordered - item.supplied;
              const stockItem = products.find(p => p.id === item.product_id);
              const stockAvailable = stockItem ? stockItem.current_stock : 0;
              return (
                <div key={index} className="flex flex-col sm:flex-row gap-4 items-start sm:items-center bg-stone-50 p-4 rounded-lg border border-stone-100 shadow-sm">
                  <div className="flex-1 w-full">
                    <p className="font-semibold text-stone-800">{item.product_name || stockItem?.name || item.product_id}</p>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm text-stone-500">
                      <span className="flex items-center gap-1"><ShoppingCart size={14} className="text-stone-400" /> Ordered: {item.ordered}</span>
                      <span className="flex items-center gap-1"><CheckCircle2 size={14} className="text-emerald-500" /> Supplied: {item.supplied}</span>
                      <span className="flex items-center gap-1 font-medium text-amber-600"><AlertTriangle size={14} /> Pending: {remaining}</span>
                      <span className="flex items-center gap-1"><Package size={14} className="text-indigo-400" /> In Stock: {stockAvailable}</span>
                    </div>
                  </div>
                  <div className="w-full sm:w-40 bg-white p-2 rounded-md border border-stone-200">
                    <Label className="text-xs text-stone-500 mb-1 block">Dispatch Qty</Label>
                    <Input
                      type="number"
                      step="0.01"
                      className="h-8 shadow-inner"
                      max={Math.min(remaining, stockAvailable)}
                      min="0"
                      value={item.dispatch}
                      onChange={(e) => {
                        const val = parseFloat(e.target.value) || 0;
                        const newD = [...dispatchData];
                        newD[index].dispatch = val;
                        setDispatchData(newD);
                      }}
                      data-testid={`dispatch-qty-${index}`}
                      disabled={remaining <= 0}
                    />
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex flex-col sm:flex-row justify-end gap-2 pt-4 border-t border-stone-100 mt-4">
            <Button type="button" variant="outline" onClick={() => setDispatchDialogOpen(false)} className="w-full sm:w-auto">Cancel</Button>
            <Button onClick={submitDispatch} disabled={dispatchData.every(d => d.dispatch <= 0)} className="w-full sm:w-auto bg-[hsl(145,60%,20%)] hover:bg-[hsl(145,60%,15%)] text-white shadow shadow-emerald-200 font-medium" data-testid="submit-dispatch-button">
              <Truck size={16} className="mr-2" />
              Confirm Dispatch
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SalesOrders;